import type { ConfirmOptions } from "../../components/ui/ConfirmProvider";
import {
  checkLineTotal,
  computeLineMath,
  lineQuantity,
  linePackUnit,
  money,
  num,
  roundQty,
  QTY_DP,
} from "../../utils/invoiceLineMath";

/**
 * D44 VR-5 on the review grid: what a person's quantity correction left on an
 * invoice, and the lock once the invoice is in stock.
 *
 * The server owns both rules (`vendorInvoice.service` `linkLineCorrection`, which
 * since D72 also recalculates a unit price correction,
 * `assertQuantitiesEditable`; `utils/vendorLineCorrection`). Nothing here
 * recalculates a figure: these only read what the server stored and decide what
 * the grid shows, so a refusal is never the first a person hears of the lock.
 */

// ─── After stock-in: quantities are locked (D44 #20, #50-#53) ────────────────

/**
 * The line cells the lock covers: the quantity under each spelling, and Total
 * Units typed on its own. The server's `quantityLockPaths` is the authority; a
 * pack edit (Units / Case, Pack Unit, Unit Size) stays allowed (#52), and the
 * Total Units it sends with it is re-derived, not typed.
 */
const QUANTITY_LINE_FIELDS: ReadonlySet<string> = new Set([
  "splitQuantity",
  "qty",
  "quantity",
  "splitItemUnits",
  "itemUnits",
]);

const QUANTITY_LINE_PATH = /^extracted\.lineItems\.\d+\.([A-Za-z0-9_]+)$/;

/** The hover text on a locked quantity cell. */
export const STOCKED_QUANTITY_LOCK_TITLE =
  "In stock: quantities can't be changed. Raise a vendor return instead.";

/** Is this line cell read-only because the invoice is already in stock? */
export function isQuantityLocked(
  invoice: { inventoryConfirmedAt?: string | null } | null | undefined,
  key: string
): boolean {
  return !!invoice?.inventoryConfirmedAt && QUANTITY_LINE_FIELDS.has(key);
}

/** `extracted.lineItems.3.splitQuantity` and the other quantity cells, by path. */
export function isQuantityEditPath(path: string): boolean {
  const m = QUANTITY_LINE_PATH.exec(String(path || ""));
  return !!m && QUANTITY_LINE_FIELDS.has(m[1]);
}

/** Where "Raise a vendor return" goes: a new return on this invoice (#53). */
export function newVendorReturnPath(vendorInvoiceId: string): string {
  return `/credit-notes/vendor/new?vendorInvoiceId=${encodeURIComponent(String(vendorInvoiceId))}`;
}

/**
 * The server's refusals that a vendor return answers: a quantity edit or an added
 * line on a stocked invoice, and a line removal after Confirm & sync. Read off
 * the machine-readable `code`, never the message.
 */
const VENDOR_RETURN_REFUSAL_CODES: ReadonlySet<string> = new Set([
  "VENDOR_INVOICE_STOCKED_QUANTITY_LOCKED",
  "LINE_REMOVAL_AFTER_CONFIRM",
]);

export function refusalOffersVendorReturn(err: unknown): boolean {
  const code = (err as { code?: unknown } | null | undefined)?.code;
  return typeof code === "string" && VENDOR_RETURN_REFUSAL_CODES.has(code);
}

// ─── Before stock-in: what the vendor printed (D44 #46, #55, #57, #58) ───────

const differs = (a: number, b: number | null, by: number) => b === null || Math.abs(a - b) >= by;

function formatQuantity(q: number): string {
  return roundQty(q).toLocaleString(undefined, { maximumFractionDigits: QTY_DP });
}

/**
 * "Vendor printed: 8 CASE, 3,694.16" for a line a person corrected, from the
 * `quantityAsPrinted` / `lineTotalAsPrinted` the server keeps the first time.
 * Null when the line was never corrected, or is back at what was printed.
 */
export function printedFiguresOf(line: any): string | null {
  if (!line || typeof line !== "object") return null;
  const printedQty = line.quantityAsPrinted === undefined ? null : num(line.quantityAsPrinted);
  const printedTotal = line.lineTotalAsPrinted === undefined ? null : num(line.lineTotalAsPrinted);
  const qtyMoved = printedQty !== null && differs(printedQty, lineQuantity(line), 1e-9);
  const totalMoved = printedTotal !== null && differs(printedTotal, computeLineMath(line).lineTotal, 0.005);
  if (!qtyMoved && !totalMoved) return null;

  const parts: string[] = [];
  if (printedQty !== null) {
    parts.push([formatQuantity(printedQty), linePackUnit(line)].filter(Boolean).join(" "));
  }
  if (printedTotal !== null) parts.push(money(printedTotal));
  return `Vendor printed: ${parts.join(", ")}`;
}

/**
 * "Vendor printed: 4,155.93" beside a stated total the corrections moved
 * (`subTotalAsPrinted`, `taxTotalAsPrinted`, `grandTotalAsPrinted`). Null when
 * nothing was kept, or the total is still what was printed.
 */
export function printedTotalOf(totals: any, key: string): string | null {
  const t = totals && typeof totals === "object" ? totals : {};
  const kept = t[`${key}AsPrinted`];
  if (kept === undefined) return null;
  const printed = num(kept);
  if (printed === null || !differs(printed, num(t[key]), 0.005)) return null;
  return `Vendor printed: ${money(printed)}`;
}

/** One stored `extracted.validation.printedLineFlags` entry (#58). */
export type PrintedLineFlag = {
  lineNo: unknown;
  description?: string;
  issue: string;
  printed?: number;
  /** D79: on a line answered "No", the line total as billed (a Line Total typed after "No" included). */
  billed?: number;
  corrected?: number;
  overcharge?: number;
};

/**
 * The stored warning for this line, if its printed total did not add up as
 * printed. Matched by line number, as the page matches the server's
 * `lineFlags`; the server numbers a line without one by position (`idx + 1`),
 * so the same fallback is read here.
 */
export function printedLineFlagFor(validation: any, line: any, index: number): PrintedLineFlag | null {
  const flags: any[] = Array.isArray(validation?.printedLineFlags) ? validation.printedLineFlags : [];
  const lineNo = line?.lineNo ?? index + 1;
  const match = flags.find(
    (f) =>
      f?.lineNo != null &&
      String(f.lineNo) === String(lineNo) &&
      typeof f.issue === "string" &&
      f.issue.trim() !== ""
  );
  return match || null;
}

/**
 * The question before a line is removed. Removal is for a line that is not
 * goods, and it does not move the totals (#57); goods that never arrived are
 * corrected to 0, which does (#54).
 */
export function lineRemovalQuestion(row: any, index: number): ConfirmOptions {
  const name = String(row?.itemName || row?.description || "").trim();
  return {
    tone: "error",
    title: `Remove line ${row?.lineNo ?? index + 1}${name ? ` (${name})` : ""} from this invoice?`,
    summary:
      "Use this for a line that is not goods, such as a heading or a freight row. The totals stay as " +
      "printed and are checked again without it. A copy is kept on the invoice.",
    hint: "If these goods never arrived, set the quantity to 0 instead: that takes them off the totals.",
    confirmLabel: "Remove line",
    cancelLabel: "Keep it",
  };
}

// ─── D77: did the supplier print the corrected price? ─────────────────────────

/** The line field the answer is stored in (server: `vendorInvoice.service` `linkPriceAnswer`). */
const SUPPLIER_PRICE_ANSWER = "supplierPrintedCorrectedPrice";
const UNIT_PRICE_PATH = /^extracted\.lineItems\.(\d+)\.unitPrice$/;

/**
 * D77 (owner, 2026-09-26; Q13): a Unit Price correction before stock-in that
 * makes a printed line that did NOT add up now add up. 8 x 10.00 printed as
 * 120.00, 15.00 typed: either the AI misread 15.00, or the supplier printed
 * 10.00 and multiplied wrong. Only the reviewer, holding the paper, can say.
 *
 * Judged on the STORED line (`stored`), as the server's #58 check
 * (`invoiceValidation.printedLineFlags`) judges it: its printed quantity, total
 * and tax, at the price printed (`unitPriceBeforeCorrection`, else the price
 * it holds). Not asked after stock-in (a price edit then recalculates nothing),
 * nor on a price worked out from the line total (D75: the supplier printed no
 * price to ask about).
 *
 * The confirm button is "No", so a question that is closed without an answer
 * leaves the line as an unanswered one has always been read: the AI misread it.
 */
export function supplierPriceQuestion(
  stored: any,
  value: any,
  { inStock }: { inStock?: boolean } = {}
): ConfirmOptions | null {
  if (inStock !== false || !stored || typeof stored !== "object" || stored.unitPriceFromLineTotal === true) return null;
  const printedTotal = stored.lineTotalAsPrinted === undefined ? stored.lineTotal : stored.lineTotalAsPrinted;
  const printedQty = stored.quantityAsPrinted === undefined ? lineQuantity(stored) : stored.quantityAsPrinted;
  const printedPrice = num(stored.unitPriceBeforeCorrection ?? stored.unitPrice);
  const newPrice = num(value);
  if (num(printedTotal) === null || printedPrice === null || newPrice === null) return null;
  const asPrintedAt = (unitPrice: number) => ({
    ...stored,
    splitQuantity: printedQty,
    qty: printedQty,
    quantity: printedQty,
    unitPrice,
    lineTotal: printedTotal,
    taxAmount: stored.taxAmountAsPrinted === undefined ? stored.taxAmount : stored.taxAmountAsPrinted,
  });
  const atPrinted = checkLineTotal(computeLineMath(asPrintedAt(printedPrice)));
  if (!atPrinted || atPrinted.ok) return null;
  if (!checkLineTotal(computeLineMath(asPrintedAt(newPrice)))?.ok) return null;
  return {
    title: `Is ${money(newPrice)} the price printed on the invoice?`,
    summary: `The line reads ${formatQuantity(num(printedQty) as number)} x ${money(printedPrice)} = ${money(num(printedTotal) as number)}, which does not add up. At ${money(newPrice)} it does.`,
    hint:
      `If the invoice says ${money(printedPrice)}, the supplier's line total is wrong: the difference ` +
      "stays flagged as an overcharge to take up with them.",
    confirmLabel: `No, it says ${money(printedPrice)}`,
    cancelLabel: "Yes, the AI misread it",
  };
}

/**
 * What to stage for one line-grid edit. A Unit Price that `supplierPriceQuestion`
 * asks about goes with the answer (`false` when the supplier printed the old
 * price); any other new price withdraws an answer still on the line, staged or
 * stored, so a stale one never rides along with it. Everything else is staged as is.
 *
 * Escape, the × or the backdrop is not an answer: `ask` returns false for it as for Cancel
 * ("Yes, the AI misread it"), so `onDismiss` tells them apart and a dismissed
 * question stages the price as one nobody was asked about.
 */
export async function lineEditOps(
  path: string,
  value: any,
  {
    storedRows,
    rows,
    inStock,
    ask,
  }: { storedRows?: any[]; rows?: any[]; inStock?: boolean; ask: (q: ConfirmOptions) => Promise<boolean> }
): Promise<{ path: string; value: any }[]> {
  const m = UNIT_PRICE_PATH.exec(path);
  if (!m) return [{ path, value }];
  const idx = Number(m[1]);
  const answerPath = `extracted.lineItems.${idx}.${SUPPLIER_PRICE_ANSWER}`;
  const question = supplierPriceQuestion(storedRows?.[idx], value, { inStock });
  if (question) {
    let dismissed = false;
    const no = await ask({ ...question, onDismiss: () => { dismissed = true; } });
    if (!dismissed) return [{ path, value }, { path: answerPath, value: !no }];
  }
  if (rows?.[idx]?.[SUPPLIER_PRICE_ANSWER] != null) return [{ path, value }, { path: answerPath, value: null }];
  return [{ path, value }];
}
